/**
 * Middleware pipeline for API requests
 */

import type { RouteContext } from './types';

export interface MiddlewareOptions {
  cors?: boolean;
  rateLimit?: boolean;
  authentication?: boolean;
}

/**
 * Apply middleware pipeline
 */
export async function applyMiddleware(
  context: RouteContext,
  options: MiddlewareOptions
): Promise<Response | null> {
  const { request } = context;

  // CORS middleware
  if (options.cors) {
    const corsResponse = await applyCors(request);
    if (corsResponse) {
      return corsResponse;
    }
  }

  // Rate limiting middleware
  if (options.rateLimit) {
    const rateLimitResponse = await applyRateLimit(context);
    if (rateLimitResponse) {
      return rateLimitResponse;
    }
  }

  // Authentication middleware
  if (options.authentication) {
    const authResponse = await applyAuthentication(context);
    if (authResponse) {
      return authResponse;
    }
  }

  return null; // Continue to route handler
}

/**
 * CORS middleware
 */
async function applyCors(request: Request): Promise<Response | null> {
  const origin = request.headers.get('origin');
  const method = request.method;

  // Handle preflight requests
  if (method === 'OPTIONS') {
    return new Response(null, {
      status: 200,
      headers: {
        'Access-Control-Allow-Origin': origin || '*',
        'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
        'Access-Control-Allow-Headers': 'Content-Type, Authorization, X-Request-ID',
        'Access-Control-Allow-Credentials': 'true',
        'Access-Control-Max-Age': '86400',
      },
    });
  }

  // CORS headers will be added to the final response
  return null;
}

/**
 * Rate limiting middleware
 */
async function applyRateLimit(context: RouteContext): Promise<Response | null> {
  // Simple in-memory rate limiting (production would use Redis/external store)
  const { request, requestId } = context;
  const clientId = getClientId(request);
  
  if (isRateLimited(clientId)) {
    return new Response(
      JSON.stringify({
        error: {
          code: 'RATE_LIMITED',
          message: 'Rate limit exceeded',
          cause: 'Too many requests from this client',
          solution: 'Wait before making another request',
          docs: 'https://vibeship.dev/docs/api#rate-limits',
        },
        meta: {
          requestId,
          timestamp: new Date().toISOString(),
        },
      }),
      {
        status: 429,
        headers: {
          'Content-Type': 'application/json',
          'Retry-After': '60',
          'X-Request-ID': requestId,
        },
      }
    );
  }

  recordRequest(clientId);
  return null;
}

/**
 * Authentication middleware
 */
async function applyAuthentication(context: RouteContext): Promise<Response | null> {
  const { request, requestId } = context;
  const authHeader = request.headers.get('authorization');

  if (!authHeader || !authHeader.startsWith('Bearer ')) {
    return new Response(
      JSON.stringify({
        error: {
          code: 'UNAUTHORIZED',
          message: 'Authentication required',
          cause: 'Missing or invalid authorization header',
          solution: 'Include a valid Bearer token in the Authorization header',
          docs: 'https://vibeship.dev/docs/api#authentication',
        },
        meta: {
          requestId,
          timestamp: new Date().toISOString(),
        },
      }),
      {
        status: 401,
        headers: {
          'Content-Type': 'application/json',
          'WWW-Authenticate': 'Bearer',
          'X-Request-ID': requestId,
        },
      }
    );
  }

  // Validate token (placeholder - implement your auth logic)
  const token = authHeader.slice(7);
  if (!isValidToken(token)) {
    return new Response(
      JSON.stringify({
        error: {
          code: 'INVALID_TOKEN',
          message: 'Invalid authentication token',
          cause: 'The provided token is expired or invalid',
          solution: 'Obtain a new token and try again',
          docs: 'https://vibeship.dev/docs/api#authentication',
        },
        meta: {
          requestId,
          timestamp: new Date().toISOString(),
        },
      }),
      {
        status: 401,
        headers: {
          'Content-Type': 'application/json',
          'X-Request-ID': requestId,
        },
      }
    );
  }

  return null;
}

// Simple rate limiting implementation (in-memory)
const requestCounts = new Map<string, { count: number; resetTime: number }>();

function getClientId(request: Request): string {
  // In production, you might use IP address, user ID, API key, etc.
  const forwarded = request.headers.get('x-forwarded-for');
  const ip = forwarded ? forwarded.split(',')[0] : 'unknown';
  return ip;
}

function isRateLimited(clientId: string): boolean {
  const now = Date.now();
  const windowMs = 60 * 1000; // 1 minute window
  const limit = 100; // 100 requests per minute

  const record = requestCounts.get(clientId);
  
  if (!record || now > record.resetTime) {
    return false;
  }

  return record.count >= limit;
}

function recordRequest(clientId: string): void {
  const now = Date.now();
  const windowMs = 60 * 1000; // 1 minute window
  const resetTime = now + windowMs;

  const record = requestCounts.get(clientId);
  
  if (!record || now > record.resetTime) {
    requestCounts.set(clientId, { count: 1, resetTime });
  } else {
    record.count++;
  }

  // Cleanup old entries
  if (Math.random() < 0.01) { // 1% chance to cleanup
    for (const [id, rec] of requestCounts.entries()) {
      if (now > rec.resetTime) {
        requestCounts.delete(id);
      }
    }
  }
}

function isValidToken(token: string): boolean {
  // Placeholder token validation
  // In practice, you'd validate against your auth system
  return token.length > 10; // Simple check for demo
}